import { lazy, type ComponentType } from "react";
import { ChunkUnavailable } from "../../components/ui/chunkUnavailable.tsx";
import { notifyUpdateAvailable } from "../../util/appUpdate.ts";

export interface SuperAdminToolDefinition {
  id: string;
  name: string;
  component: ComponentType;
  shortcutKey?: string;
  /** Visible to users holding the restricted "SuperUserTools" flag (not just full super admins).
   *  Server-side permission checks remain authoritative; this only controls UI exposure. */
  superUserTools?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lz = (factory: () => Promise<Record<string, any>>, name: string) =>
  lazy(() =>
    factory()
      .then(m => ({ default: m[name] }))
      // Stale-after-deploy chunk → graceful placeholder + update prompt, never a throw.
      .catch(() => { notifyUpdateAvailable(true); return { default: ChunkUnavailable }; }));

const QueryViewerTool = lz(() => import("./QueryViewerTool.tsx"), "QueryViewerTool");
const QuickActionsTool = lz(() => import("./QuickActionsTool.tsx"), "QuickActionsTool");
const PageInfoTool = lz(() => import("./PageInfoTool.tsx"), "PageInfoTool");
const CacheBrowserTool = lz(() => import("./CacheBrowserTool.tsx"), "CacheBrowserTool");
const ServerCacheTool = lz(() => import("./ServerCacheTool.tsx"), "ServerCacheTool");
const GridDefinitionsTool = lz(() => import("./GridDefinitionsTool.tsx"), "GridDefinitionsTool");
const ImpersonateTool = lz(() => import("./ImpersonateTool.tsx"), "ImpersonateTool");
const UploadBrowserTool = lz(() => import("./UploadBrowserTool.tsx"), "UploadBrowserTool");
const SerialLookupTool = lz(() => import("./SerialLookupTool.tsx"), "SerialLookupTool");
const DbFeatureFlagsTool = lz(() => import("./DbFeatureFlagsTool.tsx"), "DbFeatureFlagsTool");
const SmtpTestTool = lz(() => import("./SmtpTestTool.tsx"), "SmtpTestTool");
const AnnouncementsTool = lz(() => import("./AnnouncementsTool.tsx"), "AnnouncementsTool");

export const superAdminToolDefinitions: SuperAdminToolDefinition[] = [
  {
    id: "queryviewer",
    name: "Query Viewer",
    component: QueryViewerTool,
    shortcutKey: "Q",
  },
  {
    id: "quickactions",
    name: "Quick Actions",
    component: QuickActionsTool,
    superUserTools: true,
  },
  {
    id: "pageinfo",
    name: "Page Information",
    component: PageInfoTool,
    shortcutKey: "I",
  },
  {
    id: "cachebrowser",
    name: "Cache Browser",
    component: CacheBrowserTool,
    shortcutKey: "C",
  },
  {
    id: "servercache",
    name: "Server Cache",
    component: ServerCacheTool,
    shortcutKey: "V",
  },
  {
    id: "griddefinitions",
    name: "Grid Definitions",
    component: GridDefinitionsTool,
    shortcutKey: "G",
  },
  {
    id: "impersonate",
    name: "Impersonate",
    component: ImpersonateTool,
    shortcutKey: "M",
    superUserTools: true,
  },
  {
    id: "uploadbrowser",
    name: "User File Browser",
    component: UploadBrowserTool,
    shortcutKey: "F",
    superUserTools: true,
  },
  {
    id: "seriallookup",
    name: "Serial Lookup",
    component: SerialLookupTool,
    shortcutKey: "S",
    superUserTools: true,
  },
  {
    id: "dbfeatureflags",
    name: "DB Feature Flags",
    component: DbFeatureFlagsTool,
    shortcutKey: "B",
  },
  {
    id: "smtptest",
    name: "SMTP Test",
    component: SmtpTestTool,
    shortcutKey: "E",
  },
  {
    id: "announcements",
    name: "Announcements",
    component: AnnouncementsTool,
  },
];
